// B-80 — the confirmation before "Rehacer": what will be lost, said before anything is sent.
//
// Shared by one closing (`RehacerCierreDialog`) and a whole day ("Procesar el día" on a day already
// processed), so the two say it the same way. A plain `Dialog`, as the other Cierres confirmations
// are (`SabadosLaborales`, `EnviarConsolidado`): `@mantis-sas/ui`'s `ConfirmDialog` needs a provider
// no screen here mounts. "Cancelar" closes and calls nothing — the preview behind it wrote nothing.
//
// The sections come from `utils/cierreRehacer.js`; `children` is what the caller adds above them
// (the files a closing may take).
import { AlertTriangle, RotateCcw } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  Spinner,
  Textarea,
} from "@mantis-sas/ui";

import { Mensaje } from "./ProcesoDelDia";
import { guia } from "../../../utils/cierreTours";
import { BotonGuia } from "./GuiaPantalla";

export function ConfirmarRehacer({
  open,
  onOpenChange,
  titulo,
  descripcion,
  cargando = false,
  errorCarga = "",
  aviso = "",
  secciones = [],
  children = null,
  motivo = "",
  onMotivo = () => {},
  // The day's redo records its own reason ("Rehacer el día"); only one closing asks for one.
  conMotivo = true,
  error = "",
  enviando = false,
  deshabilitado = false,
  etiquetaConfirmar,
  onConfirmar,
}) {
  const nadaQuePerder = !cargando && !errorCarga && !secciones.some((s) => s.clave !== "archivos");

  return (
    <Dialog open={open} onOpenChange={(abierto) => (enviando ? null : onOpenChange(abierto))}>
      <DialogContent className="brinks-scope font-redhat flex max-h-[90vh] max-w-lg flex-col gap-0 overflow-y-auto p-0">
        <DialogHeader className="border-b p-4 text-left">
          <div className="flex items-center gap-3">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[8px] border border-brinks-warning-border bg-brinks-warning-soft text-brinks-warning">
              <RotateCcw className="h-[18px] w-[18px]" />
            </span>
            <div className="min-w-0">
              <div className="flex min-w-0 items-center gap-1">
                <DialogTitle className="text-[16px] font-semibold text-brinks-foreground">{titulo}</DialogTitle>
                <BotonGuia guiaId="dialogo-rehacer" titulo="Rehacer" texto="Rehacer vuelve a empezar desde los reportes: lo que se escribió a mano, las alertas atendidas y sus observaciones dejan de contar. Aquí ves qué se pierde antes de confirmar." />
              </div>
              {descripcion ? (
                <DialogDescription className="text-[12.5px] text-brinks-muted-foreground">{descripcion}</DialogDescription>
              ) : null}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 p-4">
          {cargando ? (
            <div className="grid place-items-center py-6">
              <Spinner className="h-5 w-5" />
            </div>
          ) : errorCarga ? (
            <Mensaje lineas={errorCarga} />
          ) : (
            <>
              {aviso ? <Mensaje tono="aviso" lineas={aviso} /> : null}
              {children}

              <section {...guia("Se pierde al rehacer", "Las correcciones y las alertas atendidas que dejan de contar, por oficina. Nada se borra: el historial queda en el cierre.")} className="space-y-2">
                <h3 className="flex items-center gap-1.5 text-[12.5px] font-semibold text-brinks-foreground">
                  <AlertTriangle className="h-3.5 w-3.5 text-brinks-warning" /> Se pierde al rehacer
                </h3>
                {nadaQuePerder ? (
                  <p className="text-[12.5px] text-brinks-muted-foreground">No hay correcciones ni alertas atendidas que se pierdan.</p>
                ) : null}
                {secciones.map((seccion) => (
                  <div key={seccion.clave} className="rounded-[10px] border bg-card">
                    <p className="border-b px-3 py-2 text-[12px] font-semibold text-brinks-foreground">{seccion.titulo}</p>
                    <ul className="max-h-[180px] divide-y overflow-y-auto">
                      {seccion.lineas.map((linea, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <li key={i} className="px-3 py-1.5">
                          <p className="text-[12.5px] text-brinks-foreground">{linea.titulo}</p>
                          {linea.detalle ? <p className="text-[11.5px] text-brinks-muted-foreground">{linea.detalle}</p> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                <p className="text-[11.5px] text-brinks-muted-foreground">
                  Nada se borra: el historial queda en el cierre, pero deja de contar.
                </p>
              </section>

              {conMotivo ? (
              <div className="space-y-1.5">
                <Label htmlFor="rehacer-motivo" className="text-[12px] font-medium text-brinks-muted-foreground">
                  Motivo (opcional)
                </Label>
                <Textarea
                  {...guia("Motivo", "Opcional: por qué se rehace. Queda en el historial del cierre.")}
                  id="rehacer-motivo"
                  value={motivo}
                  maxLength={1000}
                  rows={2}
                  disabled={enviando}
                  onChange={(e) => onMotivo(e.target.value)}
                  className="text-[13px]"
                />
              </div>
              ) : null}
            </>
          )}
          <Mensaje lineas={error} />
        </div>

        <DialogFooter className="sticky bottom-0 flex-row justify-end gap-2 border-t bg-inherit p-3 sm:space-x-0">
          <Button variant="outline" className="h-9" disabled={enviando} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button {...guia("Confirmar", "Rehace el cierre o el día con lo que ves arriba.")} className="h-9 gap-1.5" disabled={cargando || !!errorCarga || enviando || deshabilitado} onClick={onConfirmar}>
            {enviando ? <Spinner className="h-4 w-4" /> : <RotateCcw className="h-4 w-4" />}
            {enviando ? "Rehaciendo…" : etiquetaConfirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
